(() => {
  const SELECTOR = '[data-video-carousel]';

  const initializeCarousel = (section) => {
    if (!section || section.dataset.carouselInitialized === 'true') {
      return;
    }

    const track = section.querySelector('[data-carousel-track]');
    const previousButton = section.querySelector('[data-carousel-prev]');
    const nextButton = section.querySelector('[data-carousel-next]');
    const pagination = section.querySelector('[data-carousel-pagination]');
    const cards = Array.from(
      section.querySelectorAll('[data-video-card]')
    );

    if (!track || cards.length === 0) {
      return;
    }

    section.dataset.carouselInitialized = 'true';

    let snapOffsets = [];
    let scrollFrame = null;
    let resizeFrame = null;

    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartedAtBeginning = false;
    let touchStartedAtEnd = false;

    const getScrollStep = () => {
      const firstCard = cards[0];

      if (!firstCard) {
        return track.clientWidth;
      }

      const styles = window.getComputedStyle(track);
      const gap = parseFloat(
        styles.columnGap || styles.gap
      ) || 0;

      return firstCard.getBoundingClientRect().width + gap;
    };

    const getMaximumScroll = () => {
      return Math.max(
        0,
        track.scrollWidth - track.clientWidth
      );
    };

    const createSnapOffsets = () => {
      const maximumScroll = getMaximumScroll();
      const step = getScrollStep();
      const offsets = [0];

      if (maximumScroll <= 2 || step <= 0) {
        return offsets;
      }

      for (
        let offset = step;
        offset < maximumScroll - 2;
        offset += step
      ) {
        offsets.push(Math.round(offset));
      }

      const roundedMaximum = Math.round(maximumScroll);

      if (offsets[offsets.length - 1] !== roundedMaximum) {
        offsets.push(roundedMaximum);
      }

      return [...new Set(offsets)];
    };

    const getActiveIndex = () => {
      if (snapOffsets.length <= 1) {
        return 0;
      }

      const currentPosition = track.scrollLeft;
      let closestIndex = 0;
      let closestDistance = Infinity;

      snapOffsets.forEach((offset, index) => {
        const distance = Math.abs(
          currentPosition - offset
        );

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    };

    const updateControls = () => {
      const hasOverflow = getMaximumScroll() > 2;
      const activeIndex = getActiveIndex();

      section.classList.toggle(
        'has-overflow',
        hasOverflow
      );

      if (previousButton) {
        previousButton.disabled = !hasOverflow;
      }

      if (nextButton) {
        nextButton.disabled = !hasOverflow;
      }

      if (pagination) {
        pagination.hidden = !hasOverflow;

        pagination
          .querySelectorAll('[data-carousel-dot]')
          .forEach((dot, index) => {
            const isActive = index === activeIndex;

            dot.classList.toggle(
              'is-active',
              isActive
            );

            if (isActive) {
              dot.setAttribute(
                'aria-current',
                'true'
              );
            } else {
              dot.removeAttribute('aria-current');
            }
          });
      }
    };

    const goToSlide = (
      index,
      behavior = 'smooth'
    ) => {
      if (!snapOffsets.length) {
        return;
      }

      const totalSlides = snapOffsets.length;
      const loopedIndex =
        (index + totalSlides) % totalSlides;

      track.scrollTo({
        left: snapOffsets[loopedIndex],
        behavior
      });
    };

    const renderPagination = () => {
      snapOffsets = createSnapOffsets();

      if (!pagination) {
        updateControls();
        return;
      }

      pagination.innerHTML = '';

      if (snapOffsets.length <= 1) {
        pagination.hidden = true;
        updateControls();
        return;
      }

      snapOffsets.forEach((offset, index) => {
        const dot = document.createElement('button');

        dot.type = 'button';
        dot.className = 'video-carousel__dot';
        dot.dataset.carouselDot = '';

        dot.setAttribute(
          'aria-label',
          `Go to slide ${index + 1}`
        );

        dot.addEventListener('click', () => {
          goToSlide(index);
        });

        pagination.appendChild(dot);
      });

      updateControls();
    };

    const stopOtherVideos = (activeVideo) => {
      cards.forEach((card) => {
        const video = card.querySelector('video');

        if (!video || video === activeVideo) {
          return;
        }

        video.pause();
        video.controls = false;
        card.classList.remove('is-playing');
      });
    };

    previousButton?.addEventListener(
      'click',
      () => {
        goToSlide(getActiveIndex() - 1);
      }
    );

    nextButton?.addEventListener(
      'click',
      () => {
        goToSlide(getActiveIndex() + 1);
      }
    );

    /*
     * Зациклення свайпу:
     * останній → перший;
     * перший → останній.
     */
    track.addEventListener(
      'touchstart',
      (event) => {
        const touch = event.touches[0];

        if (!touch) {
          return;
        }

        const maximumScroll = getMaximumScroll();

        touchStartX = touch.clientX;
        touchStartY = touch.clientY;

        touchStartedAtBeginning =
          track.scrollLeft <= 4;

        touchStartedAtEnd =
          track.scrollLeft >= maximumScroll - 4;
      },
      { passive: true }
    );

    track.addEventListener(
      'touchend',
      (event) => {
        const touch = event.changedTouches[0];

        if (!touch || snapOffsets.length <= 1) {
          return;
        }

        const differenceX =
          touch.clientX - touchStartX;

        const differenceY =
          touch.clientY - touchStartY;

        const isHorizontalSwipe =
          Math.abs(differenceX) >= 45 &&
          Math.abs(differenceX) >
            Math.abs(differenceY);

        if (!isHorizontalSwipe) {
          return;
        }

        const lastIndex =
          snapOffsets.length - 1;

        /*
         * На останньому слайді свайп уліво.
         */
        if (
          touchStartedAtEnd &&
          differenceX < 0
        ) {
          window.setTimeout(() => {
            goToSlide(0);
          }, 80);

          return;
        }

        /*
         * На першому слайді свайп управо.
         */
        if (
          touchStartedAtBeginning &&
          differenceX > 0
        ) {
          window.setTimeout(() => {
            goToSlide(lastIndex);
          }, 80);
        }
      },
      { passive: true }
    );

    track.addEventListener(
      'scroll',
      () => {
        if (scrollFrame) {
          cancelAnimationFrame(scrollFrame);
        }

        scrollFrame = requestAnimationFrame(() => {
          updateControls();
          scrollFrame = null;
        });
      },
      { passive: true }
    );

    section
      .querySelectorAll('[data-video-play]')
      .forEach((playButton) => {
        playButton.addEventListener(
          'click',
          async () => {
            const activeCard =
              playButton.closest(
                '[data-video-card]'
              );

            const activeVideo =
              activeCard?.querySelector('video');

            if (!activeCard || !activeVideo) {
              return;
            }

            stopOtherVideos(activeVideo);

            activeVideo.playsInline = true;
            activeVideo.muted = true;
            activeVideo.controls = true;

            activeCard.classList.add(
              'is-playing'
            );

            try {
              await activeVideo.play();
            } catch (error) {
              activeVideo.controls = false;

              activeCard.classList.remove(
                'is-playing'
              );
            }
          }
        );
      });

    cards.forEach((card) => {
      const video = card.querySelector('video');

      if (!video) {
        return;
      }

      video.muted = true;
      video.playsInline = true;

      video.addEventListener('ended', () => {
        video.controls = false;
        card.classList.remove('is-playing');
      });
    });

    const handleResize = () => {
      if (resizeFrame) {
        cancelAnimationFrame(resizeFrame);
      }

      resizeFrame = requestAnimationFrame(() => {
        renderPagination();
        resizeFrame = null;
      });
    };

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver =
        new ResizeObserver(handleResize);

      resizeObserver.observe(track);
    }

    window.addEventListener(
      'resize',
      handleResize
    );

    requestAnimationFrame(renderPagination);

    window.setTimeout(
      renderPagination,
      300
    );
  };

  const initializeAllCarousels = (
    container = document
  ) => {
    if (container.matches?.(SELECTOR)) {
      initializeCarousel(container);
    }

    container
      .querySelectorAll?.(SELECTOR)
      .forEach(initializeCarousel);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initializeAllCarousels();
      }
    );
  } else {
    initializeAllCarousels();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeAllCarousels(event.target);
    }
  );
})();